import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { 
  Activity, 
  Users, 
  Clock, 
  MousePointer, 
  Search, 
  FileText,
  Smartphone,
  Monitor,
  TrendingUp,
  Eye,
  Navigation
} from 'lucide-react';
import { UserActivityService, ActivityAnalytics, UserActivity } from '@/services/analytics/userActivityService';
import { ScrollArea } from '@/components/ui/scroll-area';

interface UserActivityDashboardProps {
  timeRange?: number;
}

const COLORS = [
  'hsl(var(--primary))', 
  'hsl(var(--secondary))', 
  'hsl(var(--accent))', 
  'hsl(var(--muted))',
  'hsl(var(--destructive))',
  'hsl(var(--warning))'
];

export const UserActivityDashboard: React.FC<UserActivityDashboardProps> = ({ 
  timeRange = 30 
}) => {
  const [analytics, setAnalytics] = useState<ActivityAnalytics | null>(null);
  const [recentActivities, setRecentActivities] = useState<UserActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTimeRange, setSelectedTimeRange] = useState(timeRange);

  useEffect(() => {
    loadAnalytics();
  }, [selectedTimeRange]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const [analyticsData, recentData] = await Promise.all([
        UserActivityService.getActivityAnalytics(selectedTimeRange),
        UserActivityService.getRecentActivities(100)
      ]);
      
      setAnalytics(analyticsData);
      setRecentActivities(recentData);
    } catch (error) {
      console.error('Error loading user activity analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-2">
                <div className="h-4 bg-muted rounded w-3/4"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-muted rounded w-2/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-muted-foreground">Kunne ikke laste brukeraktivitetsdata.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const overviewCards = [
    {
      title: 'Totale aktiviteter',
      value: analytics.totalActivities.toLocaleString(),
      icon: Activity,
      description: `Siste ${selectedTimeRange} dager`,
      trend: '+12%'
    },
    {
      title: 'Unike økter',
      value: analytics.uniqueSessions.toLocaleString(),
      icon: Users,
      description: 'Unike brukerøkter',
      trend: '+5%'
    },
    {
      title: 'Gjennomsnittlig øktvarighet',
      value: `${Math.round(analytics.averageSessionDuration / 60)}m`,
      icon: Clock,
      description: 'Minutter per økt',
      trend: '+8%'
    },
    {
      title: 'Mest aktive side',
      value: analytics.mostActivePages[0]?.page_url.split('/').pop() || 'N/A',
      icon: Eye,
      description: `${analytics.mostActivePages[0]?.count || 0} visninger`,
      trend: '+15%'
    }
  ];

  const formatActivityTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('no-NO', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getActivityIcon = (activityType: string) => {
    switch (activityType) {
      case 'page_view':
      case 'page_leave':
        return <Eye className="h-4 w-4" />;
      case 'click':
        return <MousePointer className="h-4 w-4" />;
      case 'search':
        return <Search className="h-4 w-4" />;
      case 'scroll':
        return <Navigation className="h-4 w-4" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  const getActivityColor = (activityType: string) => {
    switch (activityType) {
      case 'page_view':
        return 'bg-primary/10 text-primary';
      case 'click':
        return 'bg-secondary/10 text-secondary';
      case 'search':
        return 'bg-accent/10 text-accent';
      case 'error':
        return 'bg-destructive/10 text-destructive';
      default:
        return 'bg-muted/10 text-muted-foreground';
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Brukeraktivitet</h1>
          <p className="text-muted-foreground">Detaljert innsikt i brukeradferd og engasjement</p>
        </div>
        <Tabs value={selectedTimeRange.toString()} onValueChange={(value) => setSelectedTimeRange(parseInt(value))}>
          <TabsList>
            <TabsTrigger value="7">7 dager</TabsTrigger>
            <TabsTrigger value="30">30 dager</TabsTrigger>
            <TabsTrigger value="90">90 dager</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {overviewCards.map((card, index) => (
          <Card key={index}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{card.title}</CardTitle>
              <card.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{card.value}</div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">{card.description}</p>
                <Badge variant="secondary" className="text-xs">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  {card.trend}
                </Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Analytics */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Oversikt</TabsTrigger>
          <TabsTrigger value="behavior">Brukeradferd</TabsTrigger>
          <TabsTrigger value="pages">Sideanalyse</TabsTrigger>
          <TabsTrigger value="realtime">Sanntid</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Activity Types */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Aktivitetstyper
                </CardTitle>
                <CardDescription>Fordeling av brukeraktiviteter</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={analytics.activityTypes}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                      label={({ activity_type, count }) => `${activity_type}: ${count}`}
                    >
                      {analytics.activityTypes.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Hourly Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Aktivitet per time
                </CardTitle>
                <CardDescription>Brukeraktivitet gjennom døgnet</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={analytics.hourlyActivity}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" />
                    <YAxis />
                    <Tooltip />
                    <Area 
                      type="monotone" 
                      dataKey="count" 
                      stroke="hsl(var(--primary))" 
                      fill="hsl(var(--primary))" 
                      fillOpacity={0.6} 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="behavior" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Device Types */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Smartphone className="h-5 w-5" />
                  Enhetstyper
                </CardTitle>
                <CardDescription>Enheter brukt av besøkende</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analytics.deviceTypes}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="device" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="hsl(var(--primary))" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* User Journey */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Navigation className="h-5 w-5" />
                  Brukerreise
                </CardTitle>
                <CardDescription>Mest besøkte sider i sekvens</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {analytics.userJourney.map((step, index) => (
                  <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">
                        {step.sequence}
                      </div>
                      <div>
                        <p className="font-medium">{step.page_url}</p>
                        <p className="text-sm text-muted-foreground">
                          Gj.snitt tid: {Math.round(step.avg_time / 60)}m
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="pages" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Mest aktive sider
              </CardTitle>
              <CardDescription>Sider med høyest aktivitet</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={analytics.mostActivePages.slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="page_url" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="hsl(var(--primary))" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="realtime" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Aktivitet i sanntid
              </CardTitle>
              <CardDescription>Nylige brukeraktiviteter</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[600px]">
                <div className="space-y-2">
                  {recentActivities.map((activity, index) => (
                    <div 
                      key={activity.id} 
                      className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${getActivityColor(activity.activity_type)}`}>
                          {getActivityIcon(activity.activity_type)}
                        </div>
                        <div>
                          <p className="font-medium">{activity.activity_type}</p>
                          <p className="text-sm text-muted-foreground">{activity.page_url}</p>
                          {activity.element_id && (
                            <p className="text-xs text-muted-foreground">Element: {activity.element_id}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">{formatActivityTime(activity.timestamp)}</p>
                        {activity.duration_seconds && (
                          <p className="text-xs text-muted-foreground">{activity.duration_seconds}s</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};